import {useEffect, useRef} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {httpMarkConversationMessagesAsRead} from '../http/conversations.http'
import {MUTATION_KEYS, QUERY_KEYS} from '@/queryClient'
import {errorHandler} from '@/utilities/helpers'

interface UseMarkConversationAsReadOnOpenOptions {
    onSuccess?: () => void
    onError?: (error: AxiosError) => void
    enableErrorHandling?: boolean
}

/**
 * Hook that automatically marks a conversation as read when opened.
 * Only triggers the API call if hasUnreadMessages is true.
 */
export const useMarkConversationAsReadOnOpen = (
    patientId: string,
    conversationId: string | null | undefined,
    hasUnreadMessages: boolean | null | undefined,
    options?: UseMarkConversationAsReadOnOpenOptions
) => {
    const queryClient = useQueryClient()
    const markedAsReadRef = useRef<Set<string>>(new Set())

    const markAsReadMutation = useMutation({
        mutationKey: [MUTATION_KEYS.MARK_CONVERSATION_AS_READ, conversationId],
        mutationFn: async (conversationId: string) => {
            await httpMarkConversationMessagesAsRead(conversationId)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.CONVERSATIONS, patientId]
            })
            options?.onSuccess?.()
        },
        onError: (error: AxiosError) => {
            if (options?.enableErrorHandling !== false) {
                errorHandler(error)
            }
            options?.onError?.(error)
        }
    })

    useEffect(() => {
        if (
            conversationId &&
            hasUnreadMessages === true &&
            !markAsReadMutation.isPending &&
            !markedAsReadRef.current.has(conversationId)
        ) {
            markedAsReadRef.current.add(conversationId)
            markAsReadMutation.mutate(conversationId)
        }
    }, [conversationId, hasUnreadMessages])

    useEffect(() => {
        if (conversationId && hasUnreadMessages !== true) {
            markedAsReadRef.current.delete(conversationId)
        }
    }, [conversationId, hasUnreadMessages])

    return {
        markAsReadMutation,
        isMarkingAsRead: markAsReadMutation.isPending,
        markAsRead: markAsReadMutation.mutate,
        isSuccess: markAsReadMutation.isSuccess,
        error: markAsReadMutation.error
    }
}
